import React from 'react';

import Paper from '@mui/material/Paper';

import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AlertPanel from 'ff/debrief/layouts/AlertPanel';
import { useCurrentDebrief, useCurrentTracklog } from 'ff/debrief/hooks';
import { useDebriefModal } from 'ff/debrief/modalManager';
import { DebriefAlert } from 'ff/debrief/models';

type VspeedAlertConfig = {
    priority: number;
    correspondsTo: (alert: DebriefAlert) => boolean;
    message: string;
    actionText?: string;
};

// For the initial release, we only support displaying one of the 2 alerts related to tail number and aircraft type/vspeeds.
// Because _both_ alerts will be generated if a tail number isn't set, we use some prioritization logic to determine which alert to show.
const SupportedAlertConfigurations: VspeedAlertConfig[] = [
    {
        priority: 100, // if we get both alerts from the server, we prioritize the tail number alert
        correspondsTo: (alert: DebriefAlert) =>
            alert.message.toLowerCase().startsWith('no tail number found'),
        message:
            "There's no tail number associated with your flight. This may impact the precision of your score.",
        actionText: 'Set Tail Number',
    },
    {
        priority: 50,
        correspondsTo: (alert: DebriefAlert) =>
            alert.message
                .toLowerCase()
                .startsWith('unable to determine vspeeds'),
        message:
            "We couldn't identify the aircraft type associated with your tail number. This may impact the precision of your score.",
        actionText: 'Edit Aircraft Type',
    },
];
function getAlertToDisplay(alerts: DebriefAlert[]): VspeedAlertConfig | null {
    const sortedAlerts = alerts
        .filter(alert => alert.target === 'TOP_LEVEL')
        .map(alert =>
            SupportedAlertConfigurations.find(config =>
                config.correspondsTo(alert)
            )
        )
        .filter(alert => alert !== null)
        .sort((a, b) => b.priority - a.priority);
    return sortedAlerts.length > 0 ? sortedAlerts[0] : null;
}

const VspeedAlert = () => {
    const { open: openModal } = useDebriefModal('Vspeed');
    const { debrief } = useCurrentDebrief();
    const { isOwnTracklog } = useCurrentTracklog();

    const alerts = debrief?.alerts;
    if (!alerts) {
        return null;
    }

    const handleClick = () => {
        openModal();
    };

    const alertToDisplay = getAlertToDisplay(alerts);
    if (!alertToDisplay) {
        return null;
    }

    return (
        <AlertPanel>
            <Paper
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#F6A622',
                    color: 'white',
                    py: 0.5,
                    px: 1,
                }}
                elevation={0}
            >
                <WarningRoundedIcon></WarningRoundedIcon>
                <Typography flexGrow={1} px={1} textAlign="center">
                    {alertToDisplay.message}
                </Typography>

                {alertToDisplay.actionText && isOwnTracklog && (
                    <Button
                        size="small"
                        sx={{ borderColor: 'white', color: 'white' }}
                        variant="outlined"
                        onClick={handleClick}
                    >
                        {alertToDisplay.actionText}
                    </Button>
                )}
            </Paper>
        </AlertPanel>
    );
};

export default VspeedAlert;
